<template>
  <article>
    <TourItem
      class="tour-item--1"
      contentCol="col--lg-7 col--md-10"
      mediaCol="col--lg-5"
      right
    >
      <h1>Stop Searching and Get Started with the #1 Bio Link Tool</h1>
      <p>
        Campsite.bio has Linktree beat on features. Create your space on the web
        today.
      </p>
      <p>Some of the features Linktree can't match:</p>
      <ul class="" style="margin-bottom: 1em">
        <li>⛺️ Complete control of your branding</li>
        <li>⛺️ Text, carousel and image grid links</li>
        <li>⛺️ Embed your profile on your website</li>
      </ul>
      <Button
        href="https://app.campsite.bio/create-account"
        size="large"
        color="secondary"
        variant="hard-shadow"
        >Get Started for Free</Button
      >

      <div class="tour-item__device-wrapper" slot="media">
        <ScaledDevice class="device" :width="428" :height="868" ref="preview">
          <DeviceIphone>
            <template v-if="isClient">
              <nuxt-img
                :src="currentProfileImage"
                :sizes="`xs:500px`"
                :quality="85"
                alt="Campsite.bio profile"
                ref="currentImg"
              />
              <nuxt-img
                :src="nextProfileImage"
                :sizes="`xs:500px`"
                :quality="85"
                alt="Campsite.bio profile"
                ref="nextImg"
              />
            </template>
          </DeviceIphone>
        </ScaledDevice>
      </div>
    </TourItem>

    <SeenOn />

    <section class="comparision">
      <div class="grid grid--container">
        <div class="row row--md-h-center">
          <div class="col col--lg-10 text--center">
            <h2>How we stack up on free features</h2>
            <p>
              Campsite.bio is the best Linktree alternative. We offer the most
              robust free plan available for your bio link.<br />
              This comparison chart details the differences between the free
              plans that Linktree and Campsite offer.
            </p>
          </div>
        </div>

        <table class="comparision-table">
          <thead>
            <tr>
              <th></th>
              <th>
                <Logo size="20" />
              </th>
              <th>Linktree</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th>Unlimited Links</th>
              <td>
                <span class="sr-only">Yes</span>
                <CheckIcon />
              </td>
              <td>
                <span class="sr-only">Yes</span>
                <CheckIcon />
              </td>
            </tr>
            <tr>
              <th>View/Click analytics</th>
              <td>
                <span class="sr-only">Yes</span>
                <CheckIcon />
              </td>
              <td>
                <span class="sr-only">Yes</span>
                <CheckIcon />
              </td>
            </tr>
            <tr>
              <th>Contact details</th>
              <td>
                <span class="sr-only">Yes</span>
                <CheckIcon />
              </td>
              <td>
                <span class="sr-only">Yes</span>
                <CheckIcon />
              </td>
            </tr>
            <tr>
              <th>Embed Videos</th>
              <td>
                <span class="sr-only">Yes</span>
                <CheckIcon />
              </td>
              <td>
                <span class="sr-only">Yes</span>
                <CheckIcon />
              </td>
            </tr>
            <tr>
              <th>Selection of pre-made themes</th>
              <td>
                <span class="sr-only">Yes</span>
                <CheckIcon />
              </td>
              <td>
                <span class="sr-only">Yes</span>
                <CheckIcon />
              </td>
            </tr>
            <tr>
              <th>Custom themes</th>
              <td>
                <span class="sr-only">Yes</span>
                <CheckIcon />
              </td>
              <td>
                <span class="sr-only">Yes</span>
                <CheckIcon />
              </td>
            </tr>
            <tr>
              <th>Tip Jar/Donation</th>
              <td><span class="sr-only">Yes</span> <CheckIcon /></td>
              <td>
                <span class="sr-only">Yes</span>
                <CheckIcon />
              </td>
            </tr>
            <tr>
              <th>Text block</th>
              <td><span class="sr-only">Yes</span> <CheckIcon /></td>
              <td>
                <span class="sr-only">No</span>
                -
              </td>
            </tr>
            <tr>
              <th>Image grid</th>
              <td>
                <span class="sr-only">Yes</span>
                <CheckIcon />
              </td>
              <td>
                <span class="sr-only">No</span>
                -
              </td>
            </tr>
            <tr>
              <th>Carousel <small>(single slide)</small></th>
              <td>
                <span class="sr-only">Yes</span>
                <CheckIcon />
              </td>
              <td>
                <span class="sr-only">No</span>
                -
              </td>
            </tr>
            <tr>
              <th>Shopify &amp; Spring links</th>
              <td>
                <span class="sr-only">No</span>
                -
              </td>
              <td>
                <span class="sr-only">Yes</span>
                <CheckIcon />
              </td>
            </tr>
            <tr>
              <th>Contact form link</th>
              <td>
                <span class="sr-only">No</span>
                -
              </td>
              <td>
                <span class="sr-only">Yes</span>
                <CheckIcon />
              </td>
            </tr>
            <tr>
              <th>Pin links</th>
              <td>
                <span class="sr-only">Yes</span>
                <CheckIcon />
              </td>
              <td>
                <span class="sr-only">No</span>
                -
              </td>
            </tr>
            <tr>
              <th>Archive links</th>
              <td>
                <span class="sr-only">Yes</span>
                <CheckIcon />
              </td>
              <td>
                <span class="sr-only">No</span>
                -
              </td>
            </tr>
            <tr>
              <th>Embed profile on your website</th>
              <td>
                <span class="sr-only">Yes</span>
                <CheckIcon />
              </td>
              <td>
                <span class="sr-only">No</span>
                -
              </td>
            </tr>
            <tr>
              <th>Embed Spotify/Youtube</th>
              <td>
                <span class="sr-only">Yes</span>
                <CheckIcon />
              </td>
              <td>
                <span class="sr-only">Yes</span>
                <CheckIcon />
              </td>
            </tr>
          </tbody>
        </table>

        <p class="text--center">
          <small style="font-size: 60%"
            >See anything wrong here?
            <router-link to="/contact-us">Let us know</router-link>.</small
          >
        </p>

        <div class="pricing-comparison">
          <VText variant="h3">Pricing Comparison</VText>
          <VText gutter-bottom
            >Campsite.bio makes it easy to manage multiple profiles under a
            single account. With Linktree, you have to have a separate account
            for each linktree profile.</VText
          >

          <div class="pricing-comparison__grid">
            <Card>
              <CardTitle>Campsite.bio</CardTitle>
              <CardText>
                <VText>$7 - Pro plan </VText>
                <VText>$3 - 2 additional profiles</VText>
                <VText>One account</VText>
                <VText><b>$10 / month</b></VText>
              </CardText>
            </Card>

            <Card>
              <CardTitle>Linktree</CardTitle>
              <CardText>
                <VText
                  >$9 - Three separate Pro plans (1 for each linktree)</VText
                >
                <VText>Three separate accounts</VText>
                <VText><b>$27 / month</b></VText>
              </CardText>
            </Card>
          </div>
        </div>
      </div>
    </section>

    <ProfilesSection />
    <ProSection />
  </article>
</template>

<script>
import anime from 'animejs';
import {
  Button,
  ScaledDevice,
  DeviceIphone,
  CheckIcon,
  Logo,
  Card,
  CardTitle,
  CardText,
  VText,
} from '@campsite-bio/component-lib';
import shuffle from 'lodash/shuffle';
import {
  useContext,
  computed,
  ref,
  onBeforeUnmount,
  onMounted,
} from '@nuxtjs/composition-api';

import defaultOG from '@/assets/default-og';
import TourItem from '@/components/tour-item';
import SeenOn from '@/components/seen-on-default';
import ProfilesSection from '@/components/home/profiles-section/profiles-section';
import ProSection from '@/components/home/pro-section/pro-section';

const PROFILE_IMAGES = [
  '/campsites/creators/campsite-jamiebareno.png',
  '/campsites/creators/campsite-heyjessica.png',
  '/campsites/non-profit/campsite-sundanceorg.png',
  '/campsites/creators/campsite-weratedogs.png',
  '/campsites/creators/campsite-choicehacking.png',
  '/campsites/business/campsite-shopify.png',
  '/campsites/education/campsite-arizonacals.png',
  '/campsites/creators/campsite-cassierandolph.png',
  '/campsites/business/campsite-dell.png',
  '/campsites/creators/campsite-artisiq.png',
  '/campsites/education/campsite-miadcollege.png',
  '/campsites/creators/campsite-alexokoroji.png',
  '/campsites/creators/campsite-mattevans.png',
  '/campsites/creators/campsite-adionnewilliams.png',
  '/campsites/small-business/campsite-lunasconsulting.png',
];

export default {
  head: {
    title:
      'Campsite.bio vs Linktree. Try Campsite.bio for a Best in Class Bio Link',
    meta: [
      ...defaultOG,
      {
        hid: 'description',
        name: 'description',
        content:
          'Campsite.bio offers the most robust free plan available. Get started free with our feature filled bio link tool for Instagram, Facebook, TikTok and Twitter.',
      },
    ],
  },

  components: {
    TourItem,
    ScaledDevice,
    DeviceIphone,
    SeenOn,
    Button,
    ProfilesSection,
    ProSection,
    CheckIcon,
    Logo,
    Card,
    CardTitle,
    CardText,
    VText,
  },

  setup() {
    const { $img, isDev } = useContext();
    const currentProfileImageIndex = ref(0);
    const currentImg = ref(null);
    const nextImg = ref(null);
    const profileTimeout = ref(null);

    const profileImagesGenerated = PROFILE_IMAGES.map((image) => {
      return isDev
        ? image
        : $img(image, { width: 500, quality: 85, format: 'webp' });
    });

    const profileImages = computed(() => {
      return shuffle(profileImagesGenerated);
    });

    const currentProfileImage = computed(() => {
      return profileImages.value[currentProfileImageIndex.value];
    });

    const nextProfileImage = computed(() => {
      const index =
        currentProfileImageIndex.value + 1 >= profileImages.value.length
          ? 0
          : currentProfileImageIndex.value + 1;
      return profileImages.value[index];
    });

    async function nextImage() {
      clearTimeout(profileTimeout.value);

      anime({
        targets: currentImg.value.$el,
        opacity: [1, 0],
      });
      anime({
        targets: nextImg.value.$el,
        opacity: [0, 1],
        complete() {
          currentProfileImageIndex.value =
            currentProfileImageIndex.value + 1 >= profileImages.value.length
              ? 0
              : currentProfileImageIndex.value + 1;
          profileTimeout.value = setTimeout(nextImage, 4000);
        },
      });
    }

    onMounted(() => {
      profileTimeout.value = setTimeout(nextImage, 4000);
    });

    // Before unmount, not after: Vue has already cleared the template refs by onUnmounted. Without
    // this the pending swap fired after a navigation away and read a null ref (CAM-831, same as the
    // homepage masthead in CAM-369).
    onBeforeUnmount(() => {
      clearTimeout(profileTimeout.value);
      anime.remove(currentImg.value.$el);
      anime.remove(nextImg.value.$el);
    });

    return {
      currentImg,
      nextImg,
      currentProfileImage,
      nextProfileImage,
      isClient: process.client,
    };
  },
};
</script>

<style lang="scss" scoped>
.device {
  margin: 0 auto;
  width: 250px;
  z-index: 1;

  @media (min-width: 992px) {
    width: 344px;
  }

  &::v-deep {
    img {
      position: absolute;
      height: 100%;
      width: 100%;
      top: 0;
      left: 0;

      &:first-child {
        z-index: 1;
      }
    }
  }
}

.logo {
  color: #333;
  display: inline-flex;
  text-decoration: none;
}

.page {
  padding-bottom: 3.125rem;
  padding-top: 1.875rem;
}

.comparision {
  background: #fff;
  border-bottom: 1px solid #cfd5e1;
  line-height: 1.6;
  padding: 50px 0;
}

.comparision-table {
  border-spacing: 0;
  border-collapse: collapse;
  margin: 1.875rem auto;
  max-width: 800px;
  width: 100%;

  th {
    background-color: #eee;
    font-weight: 400;

    &[scope='row'] {
      background: transparent;
    }
  }

  th,
  td {
    border: 1px solid #d9d9d9;
    font-size: 1rem;
    padding: 10px 15px;
  }

  thead th {
    padding-left: 5px;
    padding-right: 5px;
    vertical-align: top;
    padding-top: 20px;
    padding-bottom: 20px;
    text-align: center;

    &:first-child {
      background: none;
      border: none;
    }

    &:last-child {
      font-weight: 700;
      vertical-align: middle;
    }
  }

  tbody th {
    background: var(--g-color-primary);
    color: var(--g-color-on-primary);
    font-weight: 700;
    line-height: 1.2;
    text-align: right;
    width: 33.33333%;
  }

  tbody td {
    text-align: center;
    width: 16.6666666%;
  }

  tfoot td {
    padding: 15px 0 0;
    border: 0;
    text-align: center;
  }
}

.pricing-comparison {
  margin-top: 2rem;
  text-align: center;

  p {
    max-width: 600px;
    margin: 0 auto;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    grid-gap: 1.5rem;
    margin-top: 1.5rem;
  }
}

@media (min-width: 544px) {
  .comparision {
    font-size: 20px;
  }

  .comparision-table {
    tbody td {
      font-size: 20px;
    }
  }
}
</style>
